import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSchool } from '../context/SchoolContext';
import {
    ArrowLeft, User, Phone, Mail, MapPin,
    Calendar, Tag, Briefcase, FileText,
    AlertCircle, MessageSquare
} from 'lucide-react';
import './DetailPages.css';

const LeadDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { leads, getPackageName } = useSchool();

    const lead = (leads || []).find(l => String(l.id) === String(id));

    if (!lead) {
        return (
            <div className="page-container">
                <button className="btn-link flex items-center gap-2 mb-4" onClick={() => navigate('/leads')}>
                    <ArrowLeft size={18} /> Back to Leads
                </button>
                <div className="card text-center py-20">
                    <AlertCircle size={48} className="text-red mx-auto mb-4" />
                    <h2>Lead Not Found</h2>
                    <p className="text-secondary">The lead record you are looking for does not exist.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <header className="page-header">
                <div className="flex items-center gap-4">
                    <button className="f-close-btn" onClick={() => navigate('/leads')}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h2 className="page-title">{lead.name}</h2>
                        <p className="page-subtitle">Potential Student</p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <span className={`status-badge status-${(lead.status || 'New').toLowerCase()}`}>
                        {lead.status || 'New'}
                    </span>
                </div>
            </header>

            <div className="detail-grid">
                {/* Contact Info Card */}
                <div className="card">
                    <h4 className="flex items-center gap-2 mb-6 text-gold">
                        <User size={20} /> Contact Information
                    </h4>
                    <div className="info-list">
                        <div className="info-item">
                            <Phone size={18} />
                            <span>{lead.phone || 'No phone provided'}</span>
                        </div>
                        <div className="info-item">
                            <Mail size={18} />
                            <span>{lead.email || 'No email provided'}</span>
                        </div>
                        <div className="info-item border-top pt-4 mt-4">
                            <Tag size={18} />
                            <div>
                                <p className="text-xs text-secondary">SOURCE</p>
                                <p className="font-medium">{lead.source || 'Direct Walk-in'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Interest Details */}
                <div className="card">
                    <h4 className="flex items-center gap-2 mb-6 text-gold">
                        <Briefcase size={20} /> Interest & Branch
                    </h4>
                    <div className="info-grid">
                        <div className="info-block">
                            <label>Course Interest</label>
                            <p>{getPackageName ? getPackageName(typeof lead.interest === 'object' ? (lead.interest?.id || lead.interest?.name) : lead.interest) : (typeof lead.interest === 'object' ? '' : lead.interest)}</p>
                        </div>
                        <div className="info-block">
                            <label>Preferred Branch</label>
                            <p>{(lead.branch && typeof lead.branch === 'object') ? (lead.branch.branch_name || lead.branch.name || '') : (lead.branch || 'Not specified')}</p>
                        </div>
                        <div className="info-block">
                            <label>Date Enquired</label>
                            <p>{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}</p>
                        </div>
                        <div className="info-block">
                            <label>Inquiry ID</label>
                            <p>#{lead.id}</p>
                        </div>
                    </div>
                </div>

                {/* Internal Notes */}
                <div className="card span-full">
                    <h4 className="flex items-center gap-2 mb-4 text-gold">
                        <MessageSquare size={20} /> Internal Notes
                    </h4>
                    <div className="notes-container">
                        {lead.notes ? (
                            <p className="note-text">{lead.notes}</p>
                        ) : (
                            <p className="text-muted italic">No internal notes for this lead yet.</p>
                        )}
                    </div>
                    <div className="flex justify-end mt-4">
                        <p className="text-xs text-secondary italic flex items-center gap-1">
                            <FileText size={12} /> Last updated: {new Date().toLocaleDateString()}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadDetail;
